import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { List, Item, Anchor, Button } from "../elements";

/* recursively walks an item's submenu — returns true when ANY descendant
   href starts with / equals the current pathname. */
const matchesPath = (item, pathname) => {
  if (item?.href && (item.href === pathname || pathname.startsWith(item.href + "/"))) {
    return true;
  }
  if (Array.isArray(item?.submenu)) {
    return item.submenu.some((sub) => matchesPath(sub, pathname));
  }
  return false;
};

/**
 * MenuItem — recursive sidebar entry
 *  - leaf (no submenu)        → plain link
 *  - top-level w/ children    → expandable group
 *  - nested w/ children       → inline expandable group
 *
 * Stays open when the current URL matches any of its descendants.
 * Click toggle button to manually expand / collapse.
 */
export default function MenuItem({ item, nested = false }) {
  const { pathname } = useLocation();
  const hasChildren = Array.isArray(item.submenu) && item.submenu.length > 0;
  const isPathActive = useMemo(() => matchesPath(item, pathname), [item, pathname]);
  const isLeafActive = !hasChildren && item.href && pathname === item.href;

  /* Groups explicitly flagged `defaultOpen: true` in sidebar.json start
   * expanded. Used so superadmin doesn't have to click into the India
   * group every page load to find Categories / Products / Blogs. */
  const [open, setOpen] = useState(isPathActive || !!item.defaultOpen);

  // Auto-open whenever the route enters any of this menu's links.
  // Doesn't force-close — user can still toggle manually.
  useEffect(() => {
    if (isPathActive) setOpen(true);
  }, [isPathActive]);

  /* ── leaf link ───────────────────────────── */
  if (!hasChildren) {
    if (nested) {
      return (
        <Item className={`mc-sidebar-dropdown-item ${isLeafActive ? "active" : ""}`}>
          <Anchor href={item.href} className="mc-sidebar-dropdown-link">
            {item.text}
          </Anchor>
        </Item>
      );
    }
    return (
      <Item className={`mc-sidebar-menu-item ${isLeafActive ? "focused" : ""}`}>
        <Anchor
          href={item.href}
          icon={item.icon}
          text={item.text}
          badge={item.badge}
          className="mc-sidebar-menu-btn"
        />
      </Item>
    );
  }

  /* ── group with children ─────────────────── */
  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((p) => !p);
  };

  /* nested group */
  if (nested) {
    return (
      <Item className="mc-sidebar-dropdown-item">
        <button
          type="button"
          onClick={handleToggle}
          className="mc-sidebar-dropdown-link"
          style={nestedToggleStyle}
        >
          <span>{item.text}</span>
          <small
            className="material-icons"
            style={{
              fontSize: 18,
              transform: open ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform .2s",
            }}
          >
            expand_more
          </small>
        </button>
        {open && (
          <List style={nestedListStyle}>
            {item.submenu.map((sub, idx) => (
              <MenuItem key={idx} item={sub} nested />
            ))}
          </List>
        )}
      </Item>
    );
  }

  /* top-level group */
  return (
    <Item className={`mc-sidebar-menu-item ${open ? "active" : ""}`}>
      <Button
        icon={item.icon}
        text={item.text}
        badge={item.badge}
        arrow="expand_more"
        className="mc-sidebar-menu-btn"
        onClick={handleToggle}
      />
      <List className="mc-sidebar-dropdown-list">
        {item.submenu.map((sub, idx) => (
          <MenuItem key={idx} item={sub} nested />
        ))}
      </List>
    </Item>
  );
}

const nestedToggleStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  background: "transparent",
  border: "none",
  padding: 0,
  textAlign: "left",
  cursor: "pointer",
  font: "inherit",
  color: "inherit",
};

const nestedListStyle = {
  paddingLeft: 14,
  marginTop: 6,
  borderLeft: "1px dashed rgba(148, 163, 184, 0.3)",
};
